import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  ArrowUpCircle,
  ArrowDownCircle,
  Filter,
  Package,
  CreditCard,
  FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { TransactionService } from '../../services/transactionService';
import LoadingSpinner from '../common/LoadingSpinner';
import { AlertContext } from '../../contexts/AlertContext';
import TopupTransactionModal from '../topup/TopupTransactionModal';

const Operations = () => {
  const { currentUser } = useAuth();
  const { addAlert } = useContext(AlertContext);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const targetTopupOrderId = searchParams.get('topupOrderId');

  const loadTransactions = useCallback(async () => {
    if (!currentUser?.uid) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Charger toutes les transactions visibles
      const allOperations = await TransactionService.getAllUserTransactions(currentUser.uid);
      setTransactions(allOperations);
      
    } catch (error) {
      console.error('Erreur lors du chargement des transactions:', error);
      addAlert('error', 'Erreur de chargement', 'Impossible de charger l\'historique des transactions', 'high');
    } finally {
      setLoading(false);
    }
  }, [addAlert, currentUser?.uid]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 2
    }).format(amount).replace('DZD', 'DA');
  };

  const formatDate = (date) => {
    if (!date) return 'Date inconnue';
    
    let dateObj;
    if (date.toDate) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }
    
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);

    if (targetTopupOrderId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('topupOrderId');
      setSearchParams(nextParams, { replace: true });
    }
  };

  useEffect(() => {
    if (!targetTopupOrderId || transactions.length === 0) {
      return;
    }

    const targetTransaction = transactions.find(
      (transaction) =>
        String(transaction.topupOrderId || '').trim() === targetTopupOrderId ||
        String(transaction.id || '').trim() === targetTopupOrderId
    );

    if (targetTransaction) {
      setSelectedTransaction(targetTransaction);
      setIsModalOpen(true);
    }
  }, [targetTopupOrderId, transactions]);

  const isPurchaseTransaction = (transaction) => {
    if (!transaction) {
      return false;
    }

    return (
      transaction.type === 'purchase' ||
      (
        !TransactionService.isTopupTransaction(transaction) &&
        !TransactionService.isBillPaymentTransaction(transaction) &&
        (transaction.codes || transaction.productName)
      )
    );
  };

  const getTransactionTitle = (transaction) => {
    if (transaction.type === 'recharge') {
      return TransactionService.getBalanceMovementLabel(transaction);
    }

    if (TransactionService.isTopupTransaction(transaction)) {
      return TransactionService.getTopupTransactionLabel(transaction);
    }

    if (TransactionService.isBillPaymentTransaction(transaction)) {
      return TransactionService.getBillPaymentTransactionLabel(transaction);
    }

    return transaction.productName ? `Achat carte ${transaction.productName}` : 'Achat de carte';
  };

  const getTransactionDescription = (transaction) => {
    if (transaction.type === 'recharge') {
      return TransactionService.getBalanceMovementLabel(transaction);
    }

    if (TransactionService.isTopupTransaction(transaction)) {
      return transaction.reason || transaction.description || TransactionService.getTopupTransactionLabel(transaction);
    }

    if (TransactionService.isBillPaymentTransaction(transaction)) {
      const operatorLabel = TransactionService.getBillPaymentOperatorLabel(transaction);
      const billLabel = transaction.bill ? `Facture: ${transaction.bill}` : '';
      return transaction.reason || transaction.description || [operatorLabel, billLabel].filter(Boolean).join(' - ') || TransactionService.getBillPaymentTransactionLabel(transaction);
    }

    return transaction.reason || transaction.description ||
      (transaction.quantity && transaction.offerName
        ? `${transaction.quantity}x ${transaction.offerName}`
        : 'Aucune description');
  };

  const getTransactionIcon = (transaction) => {
    // Identifier le type de transaction
    if (transaction.type === 'recharge') {
      return transaction.direction === 'debit'
        ? <ArrowDownCircle className="w-5 h-5 text-danger-600" />
        : <ArrowUpCircle className="w-5 h-5 text-success-600" />;
    } else if (TransactionService.isTopupTransaction(transaction)) {
      return TransactionService.getSignedTransactionAmount(transaction) < 0
        ? <ArrowDownCircle className="w-5 h-5 text-danger-600" />
        : <ArrowUpCircle className="w-5 h-5 text-success-600" />;
    } else if (TransactionService.isBillPaymentTransaction(transaction)) {
      return TransactionService.getSignedTransactionAmount(transaction) < 0
        ? <FileText className="w-5 h-5 text-danger-600" />
        : <ArrowUpCircle className="w-5 h-5 text-success-600" />;
    } else if (isPurchaseTransaction(transaction)) {
      return <ArrowDownCircle className="w-5 h-5 text-danger-600" />;
    } else {
      return <CreditCard className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTransactionColor = (transaction) => {
    // Identifier le type de transaction
    if (transaction.type === 'recharge') {
      return transaction.direction === 'debit' ? 'text-danger-600' : 'text-success-600';
    } else if (TransactionService.isTopupTransaction(transaction)) {
      return TransactionService.getSignedTransactionAmount(transaction) < 0 ? 'text-danger-600' : 'text-success-600';
    } else if (TransactionService.isBillPaymentTransaction(transaction)) {
      return TransactionService.getSignedTransactionAmount(transaction) < 0 ? 'text-danger-600' : 'text-success-600';
    } else if (isPurchaseTransaction(transaction)) {
      return 'text-danger-600';
    } else {
      return 'text-gray-600';
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (filterType === 'all') return true;
    if (filterType === 'credit') return transaction.type === 'recharge';
    if (filterType === 'debit') {
      return (
        isPurchaseTransaction(transaction) ||
        transaction.type === 'topup_order' ||
        transaction.type === 'bill_payment'
      );
    }
    if (filterType === 'topup') return TransactionService.isTopupTransaction(transaction);
    if (filterType === 'bill') return TransactionService.isBillPaymentTransaction(transaction);
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Toutes les Opérations
        </h1>
        <p className="text-gray-600">
          Consultez l'historique complet de vos opérations financières
        </p>
      </div>

      {/* Operations */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {/* Filter */}
        <div className="mb-6">
          <div className="flex items-center space-x-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">Toutes les opérations</option>
              <option value="credit">Recharges uniquement</option>
              <option value="debit">Achats uniquement</option>
              <option value="topup">Topup generique</option>
            </select>
          </div>
        </div>

        {filteredTransactions.length > 0 ? (
          <div className="space-y-4">
            {filteredTransactions.map((transaction) => (
              <div 
                key={transaction.id} 
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleTransactionClick(transaction)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="mr-4">
                      {getTransactionIcon(transaction)}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {getTransactionTitle(transaction)}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {getTransactionDescription(transaction)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(transaction.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-semibold ${getTransactionColor(transaction)}`}>
                      {TransactionService.getSignedTransactionAmount(transaction) >= 0 ? '+' : '-'}{formatCurrency(Math.abs(TransactionService.getSignedTransactionAmount(transaction)))}
                    </p>
                    {(transaction.balanceAfter !== undefined || transaction.newBalance !== undefined) && (
                      <p className="text-xs text-gray-500">
                        Solde: {formatCurrency(transaction.balanceAfter || transaction.newBalance)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune opération</h3>
            <p className="text-gray-600">Aucune opération trouvée pour les filtres sélectionnés.</p>
          </div>
        )}
      </div>

      {/* Modal pour les détails de transaction */}
      {TransactionService.isTopupTransaction(selectedTransaction || {}) ? (
        <TopupTransactionModal
          transaction={selectedTransaction}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      ) : (
        // Modal générique pour les autres transactions (à implémenter)
        selectedTransaction && isModalOpen && (
          <div className="fixed inset-0 z-[10002] bg-black/50">
            <div className="flex min-h-full items-start justify-center overflow-y-auto px-3 py-20 sm:items-center sm:p-4">
            <div className="w-full max-w-md rounded-[28px] bg-white p-4 shadow-xl sm:p-6">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">Détails de la transaction</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Type</label>
                  <p className="text-gray-900">
                    {getTransactionTitle(selectedTransaction)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Montant</label>
                  <p className="text-xl font-semibold text-gray-900">
                    {TransactionService.getSignedTransactionAmount(selectedTransaction) >= 0 ? '+' : '-'}{formatCurrency(Math.abs(TransactionService.getSignedTransactionAmount(selectedTransaction)))}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Date</label>
                  <p className="text-gray-900">{formatDate(selectedTransaction.createdAt)}</p>
                </div>
                {(selectedTransaction.reason || selectedTransaction.description || TransactionService.isBillPaymentTransaction(selectedTransaction)) && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <p className="text-gray-900">
                      {selectedTransaction.type === 'recharge'
                        ? TransactionService.getBalanceMovementLabel(selectedTransaction)
                        : getTransactionDescription(selectedTransaction)}
                    </p>
                  </div>
                )}
                {TransactionService.isBillPaymentTransaction(selectedTransaction) && (
                  <>
                    {selectedTransaction.operatorLabel || selectedTransaction.operator ? (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Operateur</label>
                        <p className="text-gray-900">{TransactionService.getBillPaymentOperatorLabel(selectedTransaction)}</p>
                      </div>
                    ) : null}
                    {selectedTransaction.bill ? (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Reference facture</label>
                        <p className="text-gray-900">{selectedTransaction.bill}</p>
                      </div>
                    ) : null}
                    {selectedTransaction.status ? (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Statut</label>
                        <p className="text-gray-900">{selectedTransaction.status}</p>
                      </div>
                    ) : null}
                    {selectedTransaction.providerOperationId ? (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Reference operateur</label>
                        <p className="text-gray-900">{selectedTransaction.providerOperationId}</p>
                      </div>
                    ) : null}
                    {selectedTransaction.providerTransactionId ? (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Reference transaction</label>
                        <p className="text-gray-900">{selectedTransaction.providerTransactionId}</p>
                      </div>
                    ) : null}
                  </>
                )}
                {(selectedTransaction.balanceAfter !== undefined || selectedTransaction.newBalance !== undefined) && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Solde après transaction</label>
                    <p className="text-gray-900">{formatCurrency(selectedTransaction.balanceAfter || selectedTransaction.newBalance)}</p>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeModal}
                  className="w-full rounded-lg bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700 sm:w-auto"
                >
                  Fermer
                </button>
              </div>
            </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default Operations;
